import React, { useEffect, useState } from 'react';
import { Layout, Menu, Space, Dropdown, Avatar, message, Drawer, Row, Col, Divider, Typography, Modal, Popover } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LogoutOutlined, 
  UserOutlined,
  SettingOutlined,
  RiseOutlined,
  MenuOutlined,
  GithubOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import { logout } from '../../services/authService';
import { isTokenValid } from '../../utils/auth';
import type { MenuProps } from 'antd';

type MenuItem = Required<MenuProps>['items'][number];

const { Header, Footer, Content } = Layout;

interface MainLayoutProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  fullWidth?: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({ 
  children, 
  requireAuth = true,
  fullWidth = false 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [menuVisible, setMenuVisible] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (requireAuth && !isTokenValid()) {
      message.error('请先登录');
      navigate('/login');
    }
  }, [requireAuth, navigate]);

  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出',
      content: '确定要退出系统吗？',
      okText: '确认',
      cancelText: '取消',
      onOk: () => {
        logout();
        navigate('/login');
      }
    });
  };

  const menuItems = [
    {
      key: '/games',
      label: <Link to="/games">训练室</Link>,
      onClick: () => navigate('/games')
    },
    {
      key: '/profile',
      label: <Link to="/profile">个人中心</Link>,
      onClick: () => navigate('/profile')
    }
  ];

  const userMenuItems: MenuItem[] = [
    {
      key: 'progress',
      label: '个人中心',
      icon: <RiseOutlined />,
      type: 'item'
    },
    {
      key: 'divider',
      type: 'divider'
    },
    {
      key: 'logout',
      label: '退出登录',
      icon: <LogoutOutlined />,
      danger: true,
      type: 'item'
    }
  ];

  const menuProps: MenuProps = {
    theme: 'dark',
    mode: 'horizontal',
    selectedKeys: [location.pathname.startsWith('/game/') ? '/games' : location.pathname],
    style: { 
      flex: 1, 
      minWidth: isMobile ? 0 : 280,
      borderBottom: 'none'
    },
    className: 'header-menu',
    items: menuItems,
  };

  // Update header menu styles
  const menuStyle = document.createElement('style');
  menuStyle.innerHTML = `
    .header-menu.ant-menu-dark {
      background: transparent;
    }
    .header-menu.ant-menu-dark .ant-menu-item {
      padding: 0 20px;
      font-size: 15px;
      border-radius: 6px;
      margin: 0 6px;
      height: 40px;
      line-height: 40px;
      transition: all 0.3s ease;
    }
    .header-menu.ant-menu-dark .ant-menu-item:hover {
      background: rgba(255, 255, 255, 0.1) !important;
    }
    .header-menu.ant-menu-dark .ant-menu-item-selected {
      background: rgba(255, 255, 255, 0.2) !important;
    }
    .header-menu.ant-menu-dark .ant-menu-item a {
      color: rgba(255, 255, 255, 0.85);
    }
    .header-menu.ant-menu-dark .ant-menu-item-selected a {
      color: #fff;
    }
  `;
  document.head.appendChild(menuStyle);

  // 修改移动端菜单项
  const mobileMenuItems: MenuItem[] = [
    {
      key: '/games',
      icon: <TrophyOutlined />,
      label: '训练室',
      onClick: () => navigate('/games')
    },
    {
      key: '/profile',
      icon: <RiseOutlined />,
      label: '个人中心',
      onClick: () => navigate('/profile')
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
      onClick: handleLogout
    }
  ];

  return (
    <Layout className="layout" style={{ minHeight: '100vh', overflowX: 'hidden' }}>
      <Header style={{ 
        padding: 0,
        top: 0,
        zIndex: 1000,
        width: '100%',
        position: 'sticky',
        display: 'flex',
        justifyContent: 'center',
        background: '#001529'
      }}>
        <div style={{ 
          width: '100%',
          maxWidth: 1200, 
          margin: '0 auto', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: isMobile ? '0 16px' : '0 50px',
          boxSizing: 'border-box'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            minWidth: 0,
            overflow: 'hidden'
          }}>
            {isMobile ? (
              <>
                <Dropdown
                  menu={{
                    items: mobileMenuItems,
                    style: {
                      width: 200
                    }
                  }}
                  trigger={['click']}
                  placement="bottomLeft"
                  overlayStyle={{ top: '48px' }}
                  getPopupContainer={(triggerNode) => document.body}
                >
                  <MenuOutlined 
                    style={{ 
                      color: '#fff', 
                      fontSize: '20px', 
                      marginRight: '16px',
                      flexShrink: 0,
                      cursor: 'pointer'
                    }}
                  />
                </Dropdown>
                <h1 style={{ 
                  color: '#fff', 
                  margin: 0, 
                  fontSize: '18px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>超玥国际象棋训练</h1>
              </>
            ) : (
              <>
                <h1 style={{ 
                  color: '#fff', 
                  margin: 0, 
                  marginRight: '48px',
                  flexShrink: 0
                }}>超玥国际象棋训练</h1>
                <Menu {...menuProps} />
              </>
            )}
          </div>
          {isTokenValid() && (
            <Space>
              <span style={{ color: '#fff' }}>
                {localStorage.getItem('username') || '用户'}
              </span>
              <Dropdown 
                menu={{ 
                  items: userMenuItems,
                  onClick: ({ key }) => {
                    if (key === 'logout') {
                      handleLogout();
                    } else if (key === 'progress') {
                      navigate('/profile');
                    }
                  }
                }} 
                trigger={['click']}
              >
                <Avatar 
                  style={{ 
                    backgroundColor: '#ff4d4f',
                    cursor: 'pointer'
                  }} 
                  icon={<UserOutlined />}
                />
              </Dropdown>
            </Space>
          )}
        </div>
      </Header>

      <Content>
        <div style={{ 
          maxWidth: fullWidth ? 'none' : 1200, 
          margin: '0 auto', 
          width: '100%',
          padding: isMobile ? '16px' : (fullWidth ? 0 : '24px'),
          boxSizing: 'border-box'
        }}>
          {children}
        </div>
      </Content>

      <Footer style={{ 
        textAlign: 'center', 
        background: '#001529',
        padding: 0,
        color: '#fff',
        position: 'relative'
      }}>
        <div className="footer-gradient-overlay" />
        <div style={{ 
          maxWidth: 1200, 
          margin: '0 auto',
          padding: '40px 16px',
          position: 'relative',
          zIndex: 1,
          boxSizing: 'border-box'
        }}>
          <Space size={[40, 16]} wrap>
            <a href="http://www.chaoyuechess.cn/" target="_blank" rel="noopener noreferrer" className="footer-link">关于我们</a>
            <a href="https://www.chaoyuechess.com/" target="_blank" rel="noopener noreferrer" className="footer-link">超玥国象</a>
            <a href="https://appfllbnizi6634.h5.xiaoeknow.com/p/decorate/page/eyJpZCI6MzI1ODkwOCwiY2hhbm5lbF9pZCI6IiJ9" target="_blank" rel="noopener noreferrer" className="footer-link">热门课程</a>
            <Popover 
              content={
                <div style={{ textAlign: 'center' }}>
                  <img src="/contact.jpeg" alt="客服二维码" style={{ maxWidth: '200px', height: 'auto' }} />
                </div>
              }
              trigger="hover"
              placement="top"
            >
              <span className="footer-link" style={{ cursor: 'pointer' }}>客服咨询</span>
            </Popover>
          </Space>
          <div style={{ 
            marginTop: '12px', 
            opacity: 0.65,
            fontSize: '13px'
          }}>
            超玥国际象棋训练平台 ©2025 Created by ChaoYueChess
          </div>
        </div>
      </Footer>
    </Layout>
  );
};

// Update footer styles
const style = document.createElement('style');
style.innerHTML = `
  .footer-link {
    color: rgba(255, 255, 255, 0.85);
    font-size: 14px;
    transition: all 0.3s ease;
    position: relative;
    padding: 4px 0;
  }
  .footer-link:hover {
    color: #fff;
    text-shadow: 0 0 8px rgba(255, 255, 255, 0.3);
  }
  .footer-link::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  .footer-link:hover::after {
    transform: scaleX(1);
  }
  .footer-gradient-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(0, 21, 41, 0.98), rgba(0, 21, 41, 0.95));
    pointer-events: none;
  }
`;
document.head.appendChild(style);

export default MainLayout; 